import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { ApiError } from '@/lib/api-error'
import { recordFailedRequest } from '@/lib/feedback/diagnostics'
import type {
  AccountsHealth,
  AvailableProperty,
  Contact,
  ContactCreatePayload,
  ContactImportPreview,
  ContactImportResult,
  ContactSortField,
  SegmentItem,
  ContactSentEmail,
  DashboardStats,
  DeliverabilityAccountHealth,
  DisconnectResponse,
  EmailAccount,
  Enrollment,
  EnrollmentCreate,
  HubSpotSegment,
  HubSpotStatus,
  Organization,
  PaginatedResponse,
  ReactivateResponse,
  Sequence,
  SequenceCreate,
  SequenceStep,
  SequenceStepCreate,
  SequenceStepUpdate,
  SequenceUpdate,
  SyncResult,
} from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('access_token')
  const method = options?.method || 'GET'
  // A multipart upload must keep the boundary the browser generates, so the JSON
  // content type is only applied to the (far more common) serialized bodies.
  const isMultipart = typeof FormData !== 'undefined' && options?.body instanceof FormData
  let response: Response
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options?.headers,
      },
    })
  } catch (networkError) {
    recordFailedRequest({ method, url: endpoint, status: 0, requestId: null })
    Sentry.captureException(networkError, {
      extra: { endpoint, method },
    })
    throw networkError
  }

  if (response.status === 401) {
    const { handle401 } = await import('@/lib/auth-interceptor')
    response = await handle401(endpoint, options ?? {})
  }

  return readApiResponse<T>(response, endpoint, method)
}

/**
 * Turn a response into the caller's data, or throw. The request repeated after a
 * session refresh goes through here as well: its failure is an error like any other,
 * never a body to hand over as data.
 */
async function readApiResponse<T>(response: Response, endpoint: string, method: string): Promise<T> {
  if (!response.ok) {
    recordFailedRequest({
      method,
      url: endpoint,
      status: response.status,
      requestId: response.headers.get('x-request-id'),
    })
    const error = await response.json().catch(() => ({}))
    const apiError = new ApiError(
      error.detail || `API Error: ${response.status}`,
      response.status,
      typeof error.reason === 'string' ? error.reason : undefined,
    )
    if (response.status >= 500) {
      Sentry.captureException(apiError, {
        extra: { endpoint, method, status: response.status },
      })
    }
    throw apiError
  }

  if (response.status === 204) return undefined as T
  return response.json()
}

/** GET a binary resource (an attachment) with the session's credentials. */
export async function fetchApiBlob(endpoint: string): Promise<Blob> {
  const token = localStorage.getItem('access_token')
  let response = await fetch(`${API_BASE}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (response.status === 401) {
    const { handle401 } = await import('@/lib/auth-interceptor')
    response = await handle401(endpoint, {})
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(error.detail || `API Error: ${response.status}`, response.status)
  }
  return response.blob()
}

// ==================== Health ====================

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => fetchApi<{ status: string; version: string }>('/health'),
  })
}

export function useAccountHealth() {
  return useQuery({
    queryKey: ['account-health'],
    queryFn: () => fetchApi<AccountsHealth>('/api/health/accounts'),
    staleTime: 0,
  })
}

export function useAvailableProviders() {
  return useQuery({
    queryKey: ['available-providers'],
    queryFn: () => fetchApi<{ providers: string[] }>('/api/providers/available'),
    staleTime: 5 * 60 * 1000,
  })
}

// ==================== Email Accounts ====================

export function useEmailAccounts() {
  return useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => fetchApi<EmailAccount[]>('/api/email-accounts'),
  })
}

export function useDisconnectEmailAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<DisconnectResponse>(`/api/email-accounts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] })
      queryClient.invalidateQueries({ queryKey: ['account-health'] })
    },
  })
}

export function useUpdateEmailAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; is_active?: boolean; signature_html?: string | null; daily_send_limit?: number | null }) =>
      fetchApi<EmailAccount>(`/api/email-accounts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] })
    },
  })
}

/** Reads again the DNS records of the domain an account sends from (#371). */
export function useCheckSenderDomain() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<EmailAccount>(`/api/email-accounts/${id}/domain-check`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-accounts'] })
    },
  })
}

// OAuth URLs are just redirects, no hooks needed

// ==================== HubSpot ====================

export function useHubSpotStatus() {
  return useQuery({
    queryKey: ['hubspot-status'],
    queryFn: () => fetchApi<HubSpotStatus>('/api/hubspot/status'),
  })
}

export function useHubSpotAuthUrl() {
  return useQuery({
    queryKey: ['hubspot-auth-url'],
    queryFn: () => fetchApi<{ url: string }>('/api/hubspot/auth'),
    enabled: false, // Only fetch on demand
  })
}

/**
 * HubSpot lists for the sync dialog, most recently created first.
 *
 * Skips the backend cache and goes stale at once, so every time the picker is
 * enabled a list created on HubSpot a moment ago shows up (issue #318).
 */
export function useHubSpotSegments({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: ['hubspot-segments'],
    queryFn: () => fetchApi<HubSpotSegment[]>('/api/hubspot/segments?refresh=true'),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })
}

export function useDisconnectHubSpot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchApi<DisconnectResponse>('/api/hubspot/disconnect', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hubspot-status'] })
      queryClient.invalidateQueries({ queryKey: ['account-health'] })
      // Synced data is purged with the disconnect (#60): everything derived from
      // contacts, segments and their enrollments is stale from here on.
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['segments'] })
      queryClient.invalidateQueries({ queryKey: ['hubspot-segments'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unread-count'] })
    },
  })
}

export interface HubSpotPropertyInfo {
  name: string
  label: string
  type: string
  group: string
}

export interface SyncPropertyItem {
  name: string
  label: string
}

export interface SyncPropertiesConfig {
  contact: SyncPropertyItem[]
  company: SyncPropertyItem[]
}

export function useHubSpotAvailableProperties(objectType: 'contacts' | 'companies', enabled = true) {
  return useQuery({
    queryKey: ['hubspot-properties', objectType],
    queryFn: () => fetchApi<HubSpotPropertyInfo[]>(`/api/hubspot/available-properties?object_type=${objectType}`),
    enabled,
  })
}

export function useSyncPropertiesConfig() {
  return useQuery({
    queryKey: ['sync-properties'],
    queryFn: () => fetchApi<SyncPropertiesConfig>('/api/hubspot/sync-properties'),
  })
}

export function useUpdateSyncProperties() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SyncPropertiesConfig) =>
      fetchApi<SyncPropertiesConfig>('/api/hubspot/sync-properties', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sync-properties'] })
      queryClient.invalidateQueries({ queryKey: ['contacts', 'available-properties'] })
    },
  })
}

// ==================== Sequences ====================

export function useSequences() {
  return useQuery({
    queryKey: ['sequences'],
    queryFn: () => fetchApi<Sequence[]>('/api/sequences'),
  })
}

export function useSequence(id: string | undefined) {
  return useQuery({
    queryKey: ['sequences', id],
    queryFn: () => fetchApi<Sequence>(`/api/sequences/${id}`),
    enabled: !!id,
  })
}

export function useCreateSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SequenceCreate) =>
      fetchApi<Sequence>('/api/sequences', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
    },
  })
}

export function useUpdateSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SequenceUpdate }) =>
      fetchApi<Sequence>(`/api/sequences/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', id] })
    },
  })
}

export function useDeleteSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/api/sequences/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
    },
  })
}

export function useActivateSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<Sequence>(`/api/sequences/${id}/activate`, { method: 'POST' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', id] })
    },
  })
}

export function usePauseSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<Sequence>(`/api/sequences/${id}/pause`, { method: 'POST' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', id] })
    },
  })
}

export function useResumeSequence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<Sequence>(`/api/sequences/${id}/resume`, { method: 'POST' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', id] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
    },
  })
}

// ==================== Sequence Steps ====================

export function useSequenceSteps(sequenceId: string | undefined) {
  return useQuery({
    queryKey: ['sequences', sequenceId, 'steps'],
    queryFn: () => fetchApi<SequenceStep[]>(`/api/sequences/${sequenceId}/steps`),
    enabled: !!sequenceId,
  })
}

export function useCreateStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, data }: { sequenceId: string; data: SequenceStepCreate }) =>
      fetchApi<SequenceStep>(`/api/sequences/${sequenceId}/steps`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId, 'steps'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId] })
    },
  })
}

export function useUpdateStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      sequenceId,
      stepId,
      data,
    }: {
      sequenceId: string
      stepId: string
      data: SequenceStepUpdate
    }) =>
      fetchApi<SequenceStep>(`/api/sequences/${sequenceId}/steps/${stepId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId, 'steps'] })
    },
  })
}

export function useDeleteStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, stepId }: { sequenceId: string; stepId: string }) =>
      fetchApi(`/api/sequences/${sequenceId}/steps/${stepId}`, { method: 'DELETE' }),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId, 'steps'] })
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId] })
    },
  })
}

export function useReorderSteps() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sequenceId, stepIds }: { sequenceId: string; stepIds: string[] }) =>
      fetchApi(`/api/sequences/${sequenceId}/steps/reorder`, {
        method: 'POST',
        body: JSON.stringify({ step_ids: stepIds }),
      }),
    onSuccess: (_, { sequenceId }) => {
      queryClient.invalidateQueries({ queryKey: ['sequences', sequenceId, 'steps'] })
    },
  })
}

// ==================== Signatures ====================

export interface SignatureData {
  id: string
  user_id: string
  name: string
  body_html: string
  is_default: boolean
  created_at: string
}

export function useSignatures() {
  return useQuery({
    queryKey: ['signatures'],
    queryFn: () => fetchApi<SignatureData[]>('/api/signatures'),
  })
}

export function useCreateSignature() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; body_html: string; is_default?: boolean }) =>
      fetchApi<SignatureData>('/api/signatures', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['signatures'] }) },
  })
}

export function useUpdateSignature() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; body_html?: string; is_default?: boolean }) =>
      fetchApi<SignatureData>(`/api/signatures/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['signatures'] }) },
  })
}

export function useDeleteSignature() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => fetchApi(`/api/signatures/${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['signatures'] }) },
  })
}

// ==================== Step Attachments ====================

export interface StepAttachmentData {
  id: string
  step_id: string
  filename: string
  content_type: string
  file_size: number
  created_at: string
}

export function useStepAttachments(stepId: string | undefined) {
  return useQuery({
    queryKey: ['step-attachments', stepId],
    queryFn: () => fetchApi<StepAttachmentData[]>(`/api/steps/${stepId}/attachments`),
    enabled: !!stepId,
  })
}

export function useUploadStepAttachment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ stepId, file }: { stepId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const token = localStorage.getItem('access_token')
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/steps/${stepId}/attachments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<StepAttachmentData>
    },
    onSuccess: (_, { stepId }) => {
      queryClient.invalidateQueries({ queryKey: ['step-attachments', stepId] })
    },
  })
}

export function useDeleteStepAttachment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ stepId, attachmentId }: { stepId: string; attachmentId: string }) =>
      fetchApi(`/api/steps/${stepId}/attachments/${attachmentId}`, { method: 'DELETE' }),
    onSuccess: (_, { stepId }) => {
      queryClient.invalidateQueries({ queryKey: ['step-attachments', stepId] })
    },
  })
}

// ==================== Contacts ====================

export function useContacts(
  page = 1,
  limit = 50,
  filters?: {
    search?: string
    segmentId?: string
    enrollmentStatus?: string
    company?: string
    hasHubspot?: string
    createdFrom?: string
    createdTo?: string
    minEnrollments?: string
    maxEnrollments?: string
    propertyKey?: string
    propertyValue?: string
    doNotContact?: string
    viewMode?: string
    sortBy?: ContactSortField
    sortOrder?: 'asc' | 'desc'
  },
  options?: {
    enabled?: boolean
    /** Tiene la pagina corrente finché arriva la nuova, mai tra segmenti diversi. */
    keepPreviousPage?: boolean
  },
) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: ['contacts', page, limit, filters],
    placeholderData: (previous, previousQuery) => {
      if (!options?.keepPreviousPage) return undefined
      const previousFilters = previousQuery?.queryKey[3] as typeof filters
      return previousFilters?.segmentId === filters?.segmentId ? previous : undefined
    },
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })
      if (filters?.search) params.set('search', filters.search)
      if (filters?.segmentId) params.set('segment_id', filters.segmentId)
      if (filters?.enrollmentStatus) params.set('enrollment_status', filters.enrollmentStatus)
      if (filters?.company) params.set('company', filters.company)
      if (filters?.hasHubspot) params.set('has_hubspot', filters.hasHubspot)
      if (filters?.createdFrom) params.set('created_from', filters.createdFrom)
      if (filters?.createdTo) params.set('created_to', filters.createdTo)
      if (filters?.minEnrollments) params.set('min_enrollments', filters.minEnrollments)
      if (filters?.maxEnrollments) params.set('max_enrollments', filters.maxEnrollments)
      if (filters?.propertyKey) params.set('property_key', filters.propertyKey)
      if (filters?.propertyValue) params.set('property_value', filters.propertyValue)
      if (filters?.doNotContact) params.set('do_not_contact', filters.doNotContact)
      if (filters?.viewMode) params.set('view_mode', filters.viewMode)
      if (filters?.sortBy) {
        params.set('sort_by', filters.sortBy)
        params.set('order', filters.sortOrder ?? 'asc')
      }
      return fetchApi<PaginatedResponse<Contact>>(`/api/contacts?${params}`)
    },
  })
}

export function useSegments(page = 1, limit = 50, search?: string) {
  return useQuery({
    queryKey: ['segments', page, limit, search],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })
      if (search) params.set('search', search)
      return fetchApi<PaginatedResponse<SegmentItem>>(`/api/segments?${params}`)
    },
  })
}

export function useSegment(id: string | undefined) {
  return useQuery({
    queryKey: ['segment', id],
    queryFn: () => fetchApi<SegmentItem>(`/api/segments/${id}`),
    enabled: !!id,
  })
}

export function useDeleteSegment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/api/segments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['segments'] })
    },
  })
}

export function useSyncContacts() {
  return useMutation({
    mutationFn: (params: { mode: 'all' | 'segment'; segment_id?: string }) =>
      fetchApi<SyncResult>('/api/contacts/sync', {
        method: 'POST',
        body: JSON.stringify(params),
      }),
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ContactCreatePayload) =>
      fetchApi<Contact>('/api/contacts', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function usePreviewContactImport() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return fetchApi<ContactImportPreview>('/api/contacts/import/preview', {
        method: 'POST',
        body,
      })
    },
  })
}

export function useImportContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, mapping }: { file: File; mapping: Record<string, string> }) => {
      const body = new FormData()
      body.append('file', file)
      body.append('mapping', JSON.stringify(mapping))
      return fetchApi<ContactImportResult>('/api/contacts/import', { method: 'POST', body })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useAvailableProperties() {
  return useQuery({
    queryKey: ['contacts', 'available-properties'],
    queryFn: () => fetchApi<AvailableProperty[]>('/api/contacts/available-properties'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => fetchApi<Contact>(`/api/contacts/${id}`),
    enabled: !!id,
  })
}

// The "do not contact" mark (#372). Setting it stops the enrollments of the
// contact in every sequence, so whatever lists them is read again.
function useDoNotContactMutation(method: 'PUT' | 'DELETE') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) =>
      fetchApi<import('@/types/api').ContactDoNotContact>(
        `/api/contacts/${contactId}/do-not-contact`,
        { method },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useSetDoNotContact() {
  return useDoNotContactMutation('PUT')
}

export function useRemoveDoNotContact() {
  return useDoNotContactMutation('DELETE')
}

export function useContactEmails(
  contactId: string | undefined,
  page = 1,
  limit = 20,
  filters?: { status?: string; search?: string },
) {
  return useQuery({
    queryKey: ['contact-emails', contactId, page, limit, filters],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })
      if (filters?.status) params.set('status', filters.status)
      if (filters?.search) params.set('search', filters.search)
      return fetchApi<PaginatedResponse<ContactSentEmail>>(
        `/api/contacts/${contactId}/emails?${params}`,
      )
    },
    enabled: !!contactId,
  })
}

// ==================== Enrollments ====================

export function useEnrollments(
  page = 1,
  limit = 50,
  filters?: { status?: string; sequence_id?: string; contact_id?: string; viewMode?: string }
) {
  return useQuery({
    queryKey: ['enrollments', page, limit, filters],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })
      if (filters?.status) params.set('status', filters.status)
      if (filters?.sequence_id) params.set('sequence_id', filters.sequence_id)
      if (filters?.contact_id) params.set('contact_id', filters.contact_id)
      if (filters?.viewMode) params.set('view_mode', filters.viewMode)
      return fetchApi<PaginatedResponse<Enrollment>>(`/api/enrollments?${params}`)
    },
  })
}

export function useSequenceEnrollments(sequenceId: string | undefined, page = 1, limit = 50) {
  return useQuery({
    queryKey: ['enrollments', 'sequence', sequenceId, page, limit],
    queryFn: () =>
      fetchApi<PaginatedResponse<Enrollment>>(
        `/api/enrollments?sequence_id=${sequenceId}&page=${page}&limit=${limit}`
      ),
    enabled: !!sequenceId,
  })
}

export function useCreateEnrollment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: EnrollmentCreate) =>
      fetchApi<Enrollment>('/api/enrollments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
    },
  })
}

export function usePreviewBulkEnrollments() {
  return useMutation({
    mutationFn: (data: { contact_ids: string[]; sequence_id: string }) =>
      fetchApi<import('@/types/api').BulkEnrollmentPreview>('/api/enrollments/bulk/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useCreateBulkEnrollments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { contact_ids: string[]; sequence_id: string; force?: boolean; start_at?: string; overrides?: import('@/types/api').EnrollOverridesMap }) =>
      fetchApi<import('@/types/api').BulkEnrollmentResult>('/api/enrollments/bulk', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
    },
  })
}

export function usePreviewSegmentEnrollment() {
  return useMutation({
    mutationFn: (data: { segment_id: string; sequence_id: string }) =>
      fetchApi<import('@/types/api').SegmentEnrollmentPreview>('/api/enrollments/bulk/from-segment/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useNormalizeEmailHtml() {
  return useMutation({
    mutationFn: (data: { html: string }) =>
      fetchApi<{ html: string }>('/api/sequences/normalize-html', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useCreateSegmentEnrollment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { segment_id: string; sequence_id: string; force?: boolean; start_at?: string; overrides?: import('@/types/api').EnrollOverridesMap }) =>
      fetchApi<import('@/types/api').BulkEnrollmentResult>('/api/enrollments/bulk/from-segment', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['sequences'] })
    },
  })
}

// The HubSpot notes of a bulk enrollment, written in background (#386): read
// every 2 seconds while some are pending. An error (an API without the
// endpoint, during a deploy) stops the polling and shows nothing.
export function useEnrollmentBatchStatus(batchId: string | null | undefined) {
  return useQuery({
    queryKey: ['enrollment-batch', batchId],
    queryFn: () =>
      fetchApi<import('@/types/api').EnrollmentBatchStatus>(
        `/api/enrollments/bulk/batches/${batchId}`,
      ),
    enabled: !!batchId,
    retry: false,
    refetchInterval: (query) =>
      query.state.status === 'error' || (query.state.data && query.state.data.pending === 0)
        ? false
        : 2_000,
  })
}

export function useSequenceContactPreview() {
  return useMutation({
    mutationFn: (data: { sequence_id: string; contact_id: string }) =>
      fetchApi<import('@/types/api').SequenceContactPreview>(
        `/api/sequences/${data.sequence_id}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({ contact_id: data.contact_id }),
        },
      ),
  })
}

/**
 * Contenuto di ogni step renderizzato dal server per un contatto, con gli
 * override del suo enrollment in corso, o per i dati di esempio
 * (`contactId` null): lo stesso render dell'invio di test (#265).
 * `gcTime: 0`: riaprendo l'anteprima dopo una modifica non si mostra
 * il contenuto vecchio.
 */
export function useSequenceStepsPreview(
  sequenceId: string,
  contactId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['sequence-steps-preview', sequenceId, contactId],
    queryFn: () =>
      fetchApi<import('@/types/api').SequenceContactPreview>(
        `/api/sequences/${sequenceId}/preview`,
        {
          method: 'POST',
          // Qui sì: è l'anteprima di ciò che l'enrollment in corso invierà.
          // L'anteprima pre-iscrizione (useSequenceContactPreview) no.
          body: JSON.stringify({ contact_id: contactId, apply_enrollment_overrides: true }),
        },
      ),
    enabled: options?.enabled ?? true,
    gcTime: 0,
  })
}

export function useEnrollmentPreview(enrollmentId: string | undefined) {
  return useQuery({
    queryKey: ['enrollment-preview', enrollmentId],
    queryFn: () =>
      fetchApi<import('@/types/api').SequenceContactPreview>(
        `/api/enrollments/${enrollmentId}/preview`,
      ),
    enabled: !!enrollmentId,
  })
}

export function useSaveStepOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      enrollment_id: string
      step_id: string
      values: import('@/types/api').StepOverrideValues
    }) =>
      fetchApi(
        `/api/enrollments/${data.enrollment_id}/steps/${data.step_id}/override`,
        {
          method: 'PUT',
          body: JSON.stringify(data.values),
        },
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-preview', vars.enrollment_id] })
    },
  })
}

export function useDeleteStepOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { enrollment_id: string; step_id: string }) =>
      fetchApi(
        `/api/enrollments/${data.enrollment_id}/steps/${data.step_id}/override`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-preview', vars.enrollment_id] })
    },
  })
}

export function useStopEnrollment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/api/enrollments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
    },
  })
}

// ==================== Organization ====================

export function useOrganization() {
  return useQuery({
    queryKey: ['organization'],
    queryFn: () => fetchApi<Organization>('/api/org/'),
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<
        Pick<Organization, 'name' | 'contact_daily_limit_enabled' | 'hubspot_bounce_reporting_enabled'>
      >) =>
      fetchApi<Organization>('/api/org/', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (organization) => {
      queryClient.setQueryData(['organization'], organization)
    },
  })
}

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team-members'],
    queryFn: () => fetchApi<import('@/types/api').TeamMember[]>('/api/org/team'),
  })
}

export function useInvitations() {
  return useQuery({
    queryKey: ['invitations'],
    queryFn: () => fetchApi<import('@/types/api').TeamInvitation[]>('/api/org/invitations'),
  })
}

export function useCreateInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      fetchApi<import('@/types/api').TeamInvitation>('/api/org/invitations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useCancelInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/api/org/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/api/org/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
    },
  })
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'member' }) =>
      fetchApi<import('@/types/api').TeamMember>(`/api/org/members/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
    },
  })
}

// ==================== Dashboard ====================

export function useDashboardStats(params?: {
  sequenceId?: string
  period?: '7d' | '30d' | '90d' | 'all'
  viewMode?: string
}) {
  const sequenceId = params?.sequenceId
  const period = params?.period ?? 'all'
  const viewMode = params?.viewMode

  return useQuery({
    queryKey: ['dashboard-stats', sequenceId, period, viewMode],
    queryFn: () => {
      const searchParams = new URLSearchParams()
      if (sequenceId) searchParams.set('sequence_id', sequenceId)
      if (period !== 'all') searchParams.set('period', period)
      if (viewMode) searchParams.set('view_mode', viewMode)
      const qs = searchParams.toString()
      return fetchApi<DashboardStats>(`/api/dashboard/stats${qs ? `?${qs}` : ''}`)
    },
    staleTime: 60_000,
  })
}

// ==================== Deliverability ====================

export function useDeliverabilityHealth() {
  return useQuery({
    queryKey: ['deliverability-health'],
    queryFn: () => fetchApi<DeliverabilityAccountHealth[]>('/api/deliverability/accounts'),
    refetchInterval: 60_000,
  })
}

export function useReactivateEnrollments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) =>
      fetchApi<ReactivateResponse>(
        `/api/deliverability/accounts/${accountId}/reactivate`,
        { method: 'POST' }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverability-health'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
  })
}
